import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCanvas } from "@/contexts/CanvasContext";
import { useChat } from "@/contexts/ChatContext";
import { useToast } from "@/hooks";
import { useNewChat } from "@/hooks/useNewChat";
import { cn } from "@/lib";
import {
  detectCanvasTypeWithAI,
  detectProgrammingLanguage,
} from "@/lib/ai/canvasTypeDetection";
import {
  useAssistantStore,
  useAuthStore,
  useModelStore,
  useThreadStore,
} from "@/store";
import {
  Check,
  FileEdit,
  Globe,
  Image,
  Settings2,
  Telescope,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Simplified tool configuration
const SIMPLE_TOOLS = [
  {
    id: "image_gen",
    name: "Image Generation",
    icon: Image,
    category: "creative",
  },
  {
    id: "web_search",
    name: "Search the web",
    icon: Globe,
    category: "web",
  },
  {
    id: "deep_research",
    name: "Run deep research",
    icon: Telescope,
    category: "research",
    isSpecial: true, // Mark as special to handle differently
  },
  {
    id: "canvas_mode",
    name: "Canvas",
    icon: FileEdit,
    category: "creative",
    isSpecial: true, // Mark as special to handle differently
  },
];

const SimpleToolBox = ({
  isLimitedMode = false,
  disableTools = false,
  disableDeepResearch = false,
  onShowAuthOverlay,
  onToolChange,
}) => {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const { getEffectiveProvider } = useModelStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { currentAssistant, updateAssistantTools, setCurrentAssistant } =
    useAssistantStore();
  const [showDeepResearchPopup, setShowDeepResearchPopup] = useState(false);

  // Track the single active tool - ALWAYS START WITH NULL (tools defaulted off)
  const [activeToolId, setActiveToolId] = useState(null);

  // Get deep research state from ChatContext
  const chatContext = useChat();
  const {
    isDeepResearchEnabled,
    deepResearchPhase,
    toggleDeepResearch,
    optOutOfDeepResearch,
    isCanvasModeEnabled,
    enableCanvasMode,
    disableCanvasMode,
    forceDisableDeepResearch,
  } = chatContext || {
    isDeepResearchEnabled: false,
    deepResearchPhase: "disabled",
    toggleDeepResearch: () => {},
    optOutOfDeepResearch: () => {},
    isCanvasModeEnabled: false,
    enableCanvasMode: () => {},
    disableCanvasMode: () => {},
    forceDisableDeepResearch: () => {},
  };

  // Get canvas state from CanvasContext
  const { canvasState, openCanvas } = useCanvas();

  // Add useNewChat hook for coordination when on /chat/new
  const { handleDirectNewChatNavigation } = useNewChat();

  // Track previous pathname to detect navigation TO /chat/new (not just being on it)
  const prevPathnameRef = useRef(location.pathname);

  // ADDED: Effect to reset all tool states when new chat is started
  useEffect(() => {
    const handleNewChatStarted = () => {
      console.log("🔄 Resetting all tool states for new chat");

      // Reset active tool ID
      setActiveToolId(null);

      // Notify parent component that tools are reset
      if (onToolChange) {
        onToolChange(null);
      }

      // Force disable deep research if it's enabled
      if (isDeepResearchEnabled && forceDisableDeepResearch) {
        forceDisableDeepResearch();
      }

      // Disable canvas mode if it's enabled
      if (isCanvasModeEnabled && disableCanvasMode) {
        disableCanvasMode();
      }

      // Close the tools popover
      setOpen(false);
    };

    // Listen for new chat events
    window.addEventListener("clear-deep-research-state", handleNewChatStarted);
    window.addEventListener("new-chat-started", handleNewChatStarted);

    // Also listen for thread changes to reset tools when switching threads
    const handleThreadChange = () => {
      // Reset tools when switching to a different thread
      setActiveToolId(null);
      if (onToolChange) {
        onToolChange(null);
      }
    };

    window.addEventListener("thread-changed", handleThreadChange);

    return () => {
      window.removeEventListener(
        "clear-deep-research-state",
        handleNewChatStarted
      );
      window.removeEventListener("new-chat-started", handleNewChatStarted);
      window.removeEventListener("thread-changed", handleThreadChange);
    };
  }, [
    isDeepResearchEnabled,
    isCanvasModeEnabled,
    forceDisableDeepResearch,
    disableCanvasMode,
    onToolChange,
  ]);

  // ADDED: Effect to reset tools when navigating TO /chat/new (not when already on it)
  // NOTE: Only reset if tools weren't intentionally enabled AFTER navigation
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathnameRef.current;
    const isNavigatingToNewChat =
      currentPath === "/chat/new" && prevPath !== "/chat/new";

    // Update the ref for next comparison
    prevPathnameRef.current = currentPath;

    // Only reset when actually navigating TO /chat/new from another page
    if (isNavigatingToNewChat) {
      // Check if canvas mode is currently enabled - if so, don't reset it
      if (isCanvasModeEnabled) {
        console.log(
          "🎨 [SimpleToolBox] Navigating to /chat/new but canvas mode is enabled - preserving state"
        );
        // Keep canvas mode active and activeToolId
        return;
      }

      console.log(
        "🔄 [SimpleToolBox] Navigating to /chat/new route - resetting all tool states",
        {
          activeToolId,
          isCanvasModeEnabled,
          isDeepResearchEnabled,
          from: prevPath,
        }
      );
      setActiveToolId(null);
      if (onToolChange) {
        onToolChange(null);
      }
    }
  }, [
    location.pathname,
    onToolChange,
    isCanvasModeEnabled,
    activeToolId,
    isDeepResearchEnabled,
  ]);

  // Check if deep research is currently running by looking at thread messages
  const { messages } = useThreadStore();
  const isDeepResearchRunning = useMemo(() => {
    if (!messages || !Array.isArray(messages)) return false;

    // Check if there's any message with deep research in progress
    return messages.some(
      (msg) =>
        msg.metadata?.isDeepResearchInProgress === true ||
        msg.metadata?.deepResearch?.status === "active"
    );
  }, [messages]);

  // Check if current provider or model restricts tools
  const currentProvider = getEffectiveProvider();
  const currentModel = useModelStore((state) => state.model);
  const isRestrictedProvider = useMemo(() => {
    // Check if provider is restricted
    const restrictedProviders = ["perplexity", "openrouter", "groq"];
    const isProviderRestricted = restrictedProviders.includes(
      currentProvider?.toLowerCase()
    );

    // Check if specific model is restricted (like deepseek-reasoner)
    const restrictedModels = [
      "deepseek-reasoner",
      "gemini-3-pro",
      "gemini-3-pro-preview",
      "qwen3-235b-a22b-thinking-2507",
    ];
    const isModelRestricted = restrictedModels.includes(
      currentModel?.toLowerCase()
    );

    return isProviderRestricted || isModelRestricted;
  }, [currentProvider, currentModel]);

  // AI-powered canvas type detection
  const detectCanvasType = useCallback(async (userInput = "") => {
    try {
      const canvasType = await detectCanvasTypeWithAI(userInput);

      // If it's code, also detect the programming language
      if (canvasType === "code") {
        const language = detectProgrammingLanguage(userInput);
        return { type: canvasType, language };
      }

      return { type: canvasType, language: "javascript" };
    } catch (error) {
      console.error("Error detecting canvas type:", error);
      return { type: "text", language: "javascript" };
    }
  }, []);

  // Canvas activation is now handled directly in the toggle logic

  // Check if model supports tools
  const modelSupportsTools = useMemo(() => {
    if (!currentAssistant) return true;
    const model = currentAssistant.model;
    if (!model) return true;

    // Add your model support logic here
    return true;
  }, [currentAssistant]);

  // Helper function to log current tool state
  const logToolState = useCallback(
    (action, toolId, isEnabling) => {
      const toolState = {
        action,
        toolId,
        isEnabling,
        timestamp: new Date().toISOString(),
        activeTools: {
          activeToolId,
          isCanvasModeEnabled,
          isDeepResearchEnabled,
          deepResearchPhase,
        },
        settings: {
          disableTools,
          disableDeepResearch,
          isLimitedMode,
          modelSupportsTools,
          currentProvider,
          currentModel,
          isRestrictedProvider,
          currentAssistant: currentAssistant
            ? {
                id: currentAssistant.id,
                name: currentAssistant.name,
                model: currentAssistant.model,
                tools: currentAssistant.tools,
              }
            : null,
        },
      };
      console.log("🔧 [Tool Selection]", toolState);
      return toolState;
    },
    [
      activeToolId,
      isCanvasModeEnabled,
      isDeepResearchEnabled,
      deepResearchPhase,
      disableTools,
      disableDeepResearch,
      isLimitedMode,
      modelSupportsTools,
      currentProvider,
      currentModel,
      isRestrictedProvider,
      currentAssistant,
    ]
  );

  // Handle tool toggle
  const handleToolToggle = useCallback(
    async (toolId) => {
      // Log tool selection attempt
      logToolState("toggle_attempt", toolId, activeToolId !== toolId);

      // Check if this tool should be disabled for restricted providers/models
      // Compatibility mode models (gemini-3-pro, qwen3 thinking) only support web_search
      // Other restricted models/providers only support deep_research
      const compatibilityModeModels = [
        "gemini-3-pro",
        "gemini-3-pro-preview",
        "qwen3-235b-a22b-thinking-2507",
      ];
      const isCompatibilityModeModel = compatibilityModeModels.includes(
        currentModel?.toLowerCase()
      );
      const isWebSearchForCompatibilityMode =
        toolId === "web_search" && isCompatibilityModeModel;
      const isDeepResearchForNonCompatibilityMode =
        toolId === "deep_research" && !isCompatibilityModeModel;

      // Sonar and sonar-pro models don't support ANY tools (including deep research)
      // Only sonar-deep-research supports deep research
      // These tools are visually disabled, so no toast needed
      const noToolsModels = ["sonar", "sonar-pro"];
      const isNoToolsModel = noToolsModels.includes(
        currentModel?.toLowerCase()
      );

      if (isNoToolsModel) {
        return; // Tools are disabled for sonar/sonar-pro - button should be greyed out
      }

      if (
        isRestrictedProvider &&
        !isWebSearchForCompatibilityMode &&
        !isDeepResearchForNonCompatibilityMode
      ) {
        const restrictionSource = ["deepseek-reasoner"].includes(
          currentModel?.toLowerCase()
        )
          ? currentModel
          : currentProvider;

        toast({
          title: "Tool Not Available",
          description: `${toolId === "canvas_mode" ? "Canvas Mode" : toolId === "image_gen" ? "Image Generation" : "Web Search"} is not available with ${restrictionSource}. Only Deep Research is supported.`,
          variant: "destructive",
        });
        return;
      }

      // Handle deep research specially
      if (toolId === "deep_research") {
        // Check if deep research is currently running
        if (isDeepResearchRunning) {
          toast({
            title: "Deep Research In Progress",
            description:
              "Only one deep research can run at a time. Please wait for the current research to complete.",
            variant: "destructive",
          });
          return;
        }

        if (disableTools || isLimitedMode || disableDeepResearch) {
          if (onShowAuthOverlay) {
            onShowAuthOverlay("Deep Research");
          } else {
            navigate("/auth/login");
          }
          return;
        }

        // Allow users to opt out of deep research at any time, including during active phases
        // This gives users control to exit the deep research flow if they want to proceed with regular chat

        // If enabling deep research, disable all other tools first
        if (!isDeepResearchEnabled) {
          console.log("🔬 [Deep Research] Enabling deep research");
          // Disable canvas mode if active
          if (isCanvasModeEnabled) {
            console.log(
              "🔬 [Deep Research] Disabling canvas mode to enable deep research"
            );
            disableCanvasMode();
          }
          // Clear any other active tool
          setActiveToolId("deep_research");
          logToolState("deep_research_enabled", toolId, true);
        } else {
          console.log("🔬 [Deep Research] Disabling deep research");
          // Disabling deep research - allow at any time
          setActiveToolId(null);

          // If we're in the awaiting_clarifications phase, clean up the clarifying questions
          if (deepResearchPhase === "awaiting_clarifications") {
            // Remove clarifying questions from the thread
            const currentMessages = useThreadStore.getState().messages || [];
            const filteredMessages = currentMessages.filter(
              (msg) =>
                !msg.metadata?.isDeepResearchClarifying &&
                !msg.metadata?.isDeepResearchPhase1
            );

            useThreadStore.setState({
              messages: filteredMessages,
              preventNewMessageClear: true,
            });
          }
          logToolState("deep_research_disabled", toolId, false);
        }

        toggleDeepResearch();

        // If we're enabling deep research on /chat/new, set a flag to ensure it's detected
        if (!isDeepResearchEnabled && location.pathname === "/chat/new") {
          // Set a temporary flag that will be checked immediately
          window.__deepResearchJustEnabled = true;
          setTimeout(() => {
            window.__deepResearchJustEnabled = false;
          }, 1000);
        }

        // Close the popover after selection
        setOpen(false);
        return;
      }

      // Handle canvas mode specially
      if (toolId === "canvas_mode") {
        if (disableTools || isLimitedMode) {
          if (onShowAuthOverlay) {
            onShowAuthOverlay("Canvas Mode");
          } else {
            navigate("/auth/login");
          }
          return;
        }

        if (isCanvasModeEnabled) {
          console.log("🎨 [Canvas] Disabling canvas mode");
          disableCanvasMode();
          setActiveToolId(null);
          logToolState("canvas_disabled", toolId, false);
        } else {
          console.log("🎨 [Canvas] Enabling canvas mode", {
            currentPath: location.pathname,
            isOnNewChat: location.pathname === "/chat/new",
          });
          // If enabling canvas mode, disable all other tools first
          if (isDeepResearchEnabled) {
            console.log("🎨 [Canvas] Disabling deep research to enable canvas");
            toggleDeepResearch();
          }
          // Clear any other active tool (web_search, image_gen)
          setActiveToolId("canvas_mode");
          enableCanvasMode();
          logToolState("canvas_enabled", toolId, true);

          // If we're on /chat/new, log that canvas mode should work
          if (location.pathname === "/chat/new") {
            console.log(
              "🎨 [Canvas] Canvas mode enabled on /chat/new - will be active when thread is created"
            );
          }
        }
        // Close the popover after selection
        setOpen(false);
        return;
      }

      // Handle web_search and image_gen tools
      if (toolId === "web_search" || toolId === "image_gen") {
        const isCurrentlyActive = activeToolId === toolId;

        if (isCurrentlyActive) {
          console.log(`🛠️ [Tool] Disabling ${toolId}`);
          // Toggle off if clicking the same tool
          setActiveToolId(null);
          if (onToolChange) {
            onToolChange(null);
          }
          logToolState(`tool_disabled_${toolId}`, toolId, false);
        } else {
          console.log(`🛠️ [Tool] Enabling ${toolId}`);
          // Disable other active tools first
          if (isDeepResearchEnabled) {
            console.log(
              `🛠️ [Tool] Disabling deep research to enable ${toolId}`
            );
            toggleDeepResearch();
          }
          if (isCanvasModeEnabled) {
            console.log(`🛠️ [Tool] Disabling canvas mode to enable ${toolId}`);
            disableCanvasMode();
          }

          // Activate the new tool
          setActiveToolId(toolId);
          if (onToolChange) {
            onToolChange(toolId);
          }
          logToolState(`tool_enabled_${toolId}`, toolId, true);
        }

        // Close the popover after selection
        setOpen(false);
        // No need to update assistant tools - they are always included
        return;
      }

      const currentTools = currentAssistant?.tools || [];

      // For other tools, we might need to update assistant tools
      // This section is kept for future extensibility
      // Currently, web_search and image_gen are always included by configureToolParams
    },
    [
      currentAssistant,
      logToolState,
      activeToolId,
      updateAssistantTools,
      setCurrentAssistant,
      toast,
      isLimitedMode,
      disableTools,
      disableDeepResearch,
      toggleDeepResearch,
      navigate,
      location,
      isCanvasModeEnabled,
      enableCanvasMode,
      disableCanvasMode,
      isRestrictedProvider,
      currentProvider,
      currentModel,
      isDeepResearchRunning,
      optOutOfDeepResearch,
      onToolChange,
    ]
  );

  // Get the currently active tool for badge display
  const getActiveTool = useMemo(() => {
    if (
      isDeepResearchEnabled ||
      deepResearchPhase === "clarifying" ||
      deepResearchPhase === "awaiting_clarifications"
      // Note: Removed "confirming" and "researching" phases since deep research is auto-disabled in Phase 2
    ) {
      return SIMPLE_TOOLS.find((tool) => tool.id === "deep_research");
    }
    if (isCanvasModeEnabled) {
      return SIMPLE_TOOLS.find((tool) => tool.id === "canvas_mode");
    }
    if (activeToolId) {
      return SIMPLE_TOOLS.find((tool) => tool.id === activeToolId);
    }
    return null;
  }, [
    isDeepResearchEnabled,
    deepResearchPhase,
    isCanvasModeEnabled,
    activeToolId,
  ]);

  // In limited mode (temp chat), render just a button that prompts sign-in
  if (isLimitedMode) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          disabled={!modelSupportsTools}
          className={cn(
            "flex items-center gap-1.5",
            "h-8 px-2",
            "rounded-md",
            "bg-transparent",
            "hover:bg-zinc-100 dark:hover:bg-zinc-800",
            "transition-all duration-200",
            "text-sm font-medium",
            "text-zinc-700 dark:text-zinc-300",
            !modelSupportsTools && "opacity-60 cursor-not-allowed"
          )}
          onClick={() => {
            onShowAuthOverlay && onShowAuthOverlay("Tools");
          }}
        >
          <Settings2 className="h-4 w-4" />
          <span>Tools</span>
        </Button>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Popover
          open={open}
          onOpenChange={(newOpen) => {
            if (!modelSupportsTools) return;
            setOpen(newOpen);
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={!modelSupportsTools}
              className={cn(
                "flex items-center gap-1.5",
                "h-8 px-2",
                "rounded-md",
                "bg-transparent",
                "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                "transition-all duration-200",
                "text-sm font-medium",
                "text-zinc-700 dark:text-zinc-300",
                !modelSupportsTools && "opacity-60 cursor-not-allowed"
              )}
            >
              <Settings2 className="h-4 w-4" />
              <span>Tools</span>
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[220px] p-0 z-[400] rounded-xl border border-gray-200/70 dark:border-zinc-600/50 bg-white/70 dark:bg-zinc-800/70 backdrop-blur-md shadow-lg shadow-black/10"
            align="start"
            side="bottom"
            sideOffset={8}
            style={{ zIndex: 400 }}
          >
            {/* Show login prompt in limited mode (temp chat) */}
            {isLimitedMode ? (
              <div className="p-3 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Sign in to access advanced tools
                </p>
                <Button
                  onClick={() =>
                    onShowAuthOverlay && onShowAuthOverlay("Tools")
                  }
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
                >
                  Sign In
                </Button>
              </div>
            ) : (
              <div>
                <TooltipProvider>
                  {SIMPLE_TOOLS.map((tool) => {
                    const isActive =
                      tool.id === "deep_research"
                        ? (isDeepResearchEnabled ||
                            deepResearchPhase === "clarifying" ||
                            deepResearchPhase === "awaiting_clarifications") &&
                          !disableDeepResearch
                        : // Note: Removed "confirming" and "researching" phases since deep research is auto-disabled in Phase 2
                          tool.id === "canvas_mode"
                          ? isCanvasModeEnabled
                          : activeToolId === tool.id;

                    // Disable all tools except:
                    // - web_search for compatibility mode models (gemini-3-pro, qwen3 thinking)
                    // - deep_research for non-compatibility mode restricted models
                    // - NO tools for sonar/sonar-pro (only sonar-deep-research supports deep research)
                    const compatibilityModeModels = [
                      "gemini-3-pro",
                      "gemini-3-pro-preview",
                      "qwen3-235b-a22b-thinking-2507",
                    ];
                    const noToolsModels = ["sonar", "sonar-pro"];
                    const isNoToolsModel = noToolsModels.includes(
                      currentModel?.toLowerCase()
                    );
                    const isCompatibilityModeModel =
                      compatibilityModeModels.includes(
                        currentModel?.toLowerCase()
                      );
                    const isWebSearchForCompatibilityMode =
                      tool.id === "web_search" && isCompatibilityModeModel;
                    const isDeepResearchForNonCompatibilityMode =
                      tool.id === "deep_research" &&
                      !isCompatibilityModeModel &&
                      !isNoToolsModel;
                    const isToolDisabled =
                      isNoToolsModel ||
                      (isRestrictedProvider &&
                        !isWebSearchForCompatibilityMode &&
                        !isDeepResearchForNonCompatibilityMode);

                    // Special handling for deep research when it's running
                    const isDeepResearchDisabled =
                      tool.id === "deep_research" &&
                      isDeepResearchRunning &&
                      !isDeepResearchEnabled;

                    const shouldShowTooltip = isDeepResearchDisabled;
                    const tooltipContent =
                      "Only one deep research can run at a time";

                    const ToolButton = (
                      <button
                        key={tool.id}
                        onClick={() => handleToolToggle(tool.id)}
                        disabled={
                          isToolDisabled ||
                          isDeepResearchDisabled ||
                          (disableTools &&
                            tool.id !== "deep_research" &&
                            tool.id !== "canvas_mode")
                        }
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2",
                          "transition-all duration-150",
                          "bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-700/70",
                          isActive && "text-[#2563eb] dark:text-[#99CEFF]",
                          !isActive && "text-gray-900 dark:text-white",
                          (isToolDisabled ||
                            isDeepResearchDisabled ||
                            (disableTools &&
                              tool.id !== "deep_research" &&
                              tool.id !== "canvas_mode")) &&
                            "opacity-60 cursor-not-allowed"
                        )}
                      >
                        <tool.icon
                          className={cn(
                            "h-4 w-4 flex-shrink-0 transition-colors duration-150",
                            isActive
                              ? "text-[#2563eb] dark:text-[#99CEFF]"
                              : "text-gray-600 dark:text-zinc-400"
                          )}
                        />
                        <span className="text-sm font-medium flex-1 text-left">
                          {tool.name}
                        </span>

                        {isActive ? (
                          <Check className="h-3 w-3 text-[#2563eb] dark:text-[#99CEFF] flex-shrink-0 transition-all duration-150" />
                        ) : null}
                      </button>
                    );

                    // Wrap with tooltip if needed
                    if (shouldShowTooltip) {
                      return (
                        <Tooltip key={tool.id}>
                          <TooltipTrigger asChild>{ToolButton}</TooltipTrigger>
                          <TooltipContent>
                            <p>{tooltipContent}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return ToolButton;
                  })}
                </TooltipProvider>

                {/* Show provider/model restriction notice */}
                {isRestrictedProvider && (
                  <div className="px-3 py-2 border-t border-zinc-200 dark:border-zinc-800">
                    <p className="text-xs text-muted-foreground">
                      {currentProvider?.toLowerCase() === "perplexity"
                        ? "Tools are not available with this model."
                        : currentModel?.toLowerCase() === "gemini-3-pro" ||
                            currentModel?.toLowerCase() ===
                              "gemini-3-pro-preview"
                          ? "Only web search is available with gemini-3-pro"
                          : currentModel?.toLowerCase() ===
                              "qwen3-235b-a22b-thinking-2507"
                            ? "Only web search is available with qwen3 thinking"
                            : `Only Deep Research is available with ${
                                ["deepseek-reasoner"].includes(
                                  currentModel?.toLowerCase()
                                )
                                  ? currentModel
                                  : currentProvider
                              }`}
                    </p>
                  </div>
                )}
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Active Tool Badge */}
        {getActiveTool && (
          <div className="flex items-center gap-1 px-2 py-1 bg-[#2563eb]/10 dark:bg-[#99CEFF]/10 border border-[#2563eb]/30 dark:border-[#99CEFF]/20 rounded-md">
            <getActiveTool.icon className="h-3 w-3 text-[#2563eb] dark:text-[#99CEFF]" />
            <span className="text-xs font-medium text-[#2563eb] dark:text-[#99CEFF]">
              {getActiveTool.name === "Search the web"
                ? "Search"
                : getActiveTool.name === "Image Generation"
                  ? "Image"
                  : getActiveTool.name === "Run deep research"
                    ? "Research"
                    : getActiveTool.name === "Canvas"
                      ? "Canvas"
                      : getActiveTool.name}
            </span>
            <button
              onClick={() => handleToolToggle(getActiveTool.id)}
              className="ml-auto flex items-center justify-center w-4 h-4 rounded-full bg-transparent hover:bg-[#2563eb]/20 dark:hover:bg-[#99CEFF]/20 transition-colors duration-150"
              title={`Remove ${getActiveTool.name}`}
            >
              <X className="h-2.5 w-2.5 text-[#2563eb] dark:text-[#99CEFF]" />
            </button>
          </div>
        )}
      </div>

      {/* Deep Research Dialog Fallback */}
      {showDeepResearchPopup && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowDeepResearchPopup(false)}
          />
          <div className="relative w-[320px] p-0 z-[9999] rounded-2xl border-0 bg-white dark:bg-zinc-900">
            <div className="overflow-hidden rounded-2xl">
              {/* Purple gradient header with particles */}
              <div className="relative h-24 bg-gradient-to-br from-purple-300 via-purple-500 via-purple-600 via-blue-500 to-purple-800 overflow-hidden">
                {/* Particle effects */}
                <div className="absolute top-2 left-4 w-2 h-2 bg-white/30 rounded-full blur-sm"></div>
                <div className="absolute top-6 right-6 w-1 h-1 bg-white/50 rounded-full"></div>
                <div className="absolute top-4 left-1/2 w-1.5 h-1.5 bg-white/20 rounded-full blur-sm"></div>
                <div className="absolute bottom-4 left-6 w-1 h-1 bg-white/40 rounded-full"></div>
                <div className="absolute top-8 right-12 w-1.5 h-1.5 bg-white/25 rounded-full blur-sm"></div>
                <div className="absolute bottom-6 right-4 w-1 h-1 bg-white/35 rounded-full"></div>
                <div className="absolute top-12 left-1/4 w-1 h-1 bg-white/45 rounded-full"></div>
                <div className="absolute bottom-2 left-1/3 w-1.5 h-1.5 bg-white/20 rounded-full blur-sm"></div>
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
              </div>

              {/* Content section */}
              <div className="p-6 space-y-4 bg-white dark:bg-zinc-900">
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">
                    Unlock Deep Research
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Conduct comprehensive research across multiple sources with
                    AI-powered analysis and insights.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate("/auth/login")}
                    className="flex-1 bg-white hover:bg-gray-50 text-black border border-gray-300 rounded-2xl"
                  >
                    Log in
                  </Button>
                  <Button
                    onClick={() => navigate("/auth/signup")}
                    className="flex-1 bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 rounded-2xl"
                    variant="outline"
                  >
                    Sign Up
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

SimpleToolBox.propTypes = {
  isLimitedMode: PropTypes.bool,
  disableTools: PropTypes.bool,
  disableDeepResearch: PropTypes.bool,
  onShowAuthOverlay: PropTypes.func,
};

export default SimpleToolBox;
